import React from 'react';
import App from './components/app/App';
import Auth, { AuthProps } from './pages/auth/Auth';
import ErrorBoundary from './components/errorboundary/ErrorBoundary';
import Footer, { FooterProps } from './components/footer/Footer';
import Header, { HeaderProps } from './components/header/Header';
import Loading, { LoadingProps } from './components/loading/Loading';

type ComponentClassAsync<T> = Promise<{ default: React.ComponentClass<T> }>;

export class ComponentFactory {
  static get<T>(component: any): new () => React.Component<T> {
    return component;
  }

  static getAsync<T>(component: any): ComponentClassAsync<T> {
    return component;
  }

  static get AccountSelectAsync() {
    return import(/* webpackChunkName: "AccountSelect" */ './components/accountselect/AccountSelect');
  }

  static get App() {
    return ComponentFactory.get(App);
  }

  static get Auth() {
    return ComponentFactory.get<AuthProps>(Auth);
  }

  static get DocumentsAsync() {
    return import(/* webpackChunkName: "Documents" */ './pages/documents/Documents');
  }

  static get SettingsAsync() {
    return import(/* webpackChunkName: "Settings" */ './pages/settings/Settings');
  }

  static get BankAccountFormAsync() {
    return import(/* webpackChunkName: "BankAccountForm" */ './components/bankaccountform/BankAccountForm');
  }

  static get InfoCardAsync() {
    return import(/* webpackChunkName: "InfoCard" */ './components/infocard/InfoCard');
  }

  static get ClaimedGromaCoinCardAsync() {
    return import(
      /* webpackChunkName: "ClaimedGromaCoinCard" */ './components/claimedgromacoincard/ClaimedGromaCoinCard'
    );
  }

  static get ClaimVerifyIdentityAsync() {
    return import(/* webpackChunkName: "ClaimVerifyIdentity" */ './pages/onboarding/ClaimVerifyIdentity');
  }

  static get DocumentItemAsync() {
    return import(/* webpackChunkName: "DocumentItem" */ './components/documentitem/DocumentItem');
  }

  static get DocumentsListAsync() {
    return import(/* webpackChunkName: "DocumentsList" */ './components/documentslist/DocumentsList');
  }

  static get EmptyAccountsAsync() {
    return import(/* webpackChunkName: "EmptyAccounts" */ './components/emptyaccounts/EmptyAccounts');
  }

  static get ErrorBoundary() {
    return ComponentFactory.get(ErrorBoundary);
  }

  static get ErrorPageAsync() {
    return import(/* webpackChunkName: "ErrorPage" */ './pages/errorpage/ErrorPage');
  }

  static get Footer() {
    return ComponentFactory.get<FooterProps>(Footer);
  }

  static get Header() {
    return ComponentFactory.get<HeaderProps>(Header);
  }

  static get HelpAsync() {
    return import(/* webpackChunkName: "Help" */ './pages/help/Help');
  }

  static get IdentityVerificationModalAsync() {
    return import(
      /* webpackChunkName: "IdentityVerificationModal" */ './components/identityverification/IdentityVerificationModal'
    );
  }

  static get HomeAsync() {
    return import(/* webpackChunkName: "Home" */ './pages/home/Home');
  }

  static get InvestAsync() {
    return import(/* webpackChunkName: "Invest" */ './pages/invest/Invest');
  }

  static get Loading() {
    return ComponentFactory.get<LoadingProps>(Loading);
  }

  static get OverviewAsync() {
    return import(/* webpackChunkName: "Overview" */ './pages/overview/Overview');
  }

  static get PortfolioAsync() {
    return import(/* webpackChunkName: "Portfolio" */ './components/portfolio/Portfolio');
  }

  static get FundInfoAsync() {
    return import(/* webpackChunkName: "FundInfo" */ './components/fundinfo/FundInfo');
  }

  static get GromaCoinConfettiAsync() {
    return import(/* webpackChunkName: "GromaCoinConfetti" */ './components/gromacoinconfetti/GromaCoinConfetti');
  }

  static get SideNavAsync() {
    return import(/* webpackChunkName: "SideNav" */ './components/sidenav/SideNav');
  }

  static get TransactionItemAsync() {
    return import(/* webpackChunkName: "TransactionItem" */ './components/transactionitem/TransactionItem');
  }

  static get TransactionsListAsync() {
    return import(/* webpackChunkName: "TransactionsList" */ './components/transactionslist/TransactionsList');
  }

  static get WalletAsync() {
    return import(/* webpackChunkName: "Wallet" */ './pages/wallet/Wallet');
  }

  static get BankAccountInfoAsync() {
    return import(/* webpackChunkName: "BankAccountInfo" */ './components/bankaccountinfo/BankAccountInfo');
  }

  static get AccreditationFlowAsync() {
    return import(/* webpackChunkName: "AccreditationFlow" */ './pages/accreditation/AccreditationFlow');
  }

  static get AccreditedInvestorAsync() {
    return import(/* webpackChunkName: "AccreditedInvestor" */ './pages/onboarding/AccreditedInvestor');
  }

  static get AddEntityContactInfoAsync() {
    return import(/* webpackChunkName: "AddEntityContactInfo" */ './pages/onboarding/AddEntityContactInfo');
  }

  static get AddEntityDetailsAsync() {
    return import(/* webpackChunkName: "AddEntityDetails" */ './pages/onboarding/AddEntityDetails');
  }

  static get CreateInvestmentAccountAsync() {
    return import(/* webpackChunkName: "CreateInvestmentAccount" */ './pages/onboarding/CreateInvestmentAccount');
  }

  static get GetStartedAsync() {
    return import(/* webpackChunkName: "GetStarted" */ './pages/onboarding/GetStarted');
  }

  static get GromaCoinWelcomeAsync() {
    return import(/* webpackChunkName: "GromaCoinWelcome" */ './pages/gromacoin/welcome/Welcome');
  }

  static get GromaCoinTransactionAsync() {
    return import(/* webpackChunkName: "GromaCoinTransaction" */ './pages/gromacoin/transaction/Transaction');
  }

  static get GromaCoinNextStepsAsync() {
    return import(/* webpackChunkName: "GromaCoinNextSteps" */ './pages/gromacoin/nextsteps/NextSteps');
  }

  static get GromaCoinVerifyAsync() {
    return import(/* webpackChunkName: "GromaCoinVerify" */ './pages/gromacoin/verify/Verify');
  }

  static get GromaCoinWaitlistAsync() {
    return import(/* webpackChunkName: "GromaCoinWaitlist" */ './pages/gromacoin/waitlist/Waitlist');
  }

  static get SelectEntityTypeAsync() {
    return import(/* webpackChunkName: "SelectEntityType" */ './pages/onboarding/SelectEntityType');
  }

  static get VerifyIdentityAsync() {
    return import(/* webpackChunkName: "VerifyIdentity" */ './pages/onboarding/VerifyIdentity');
  }

  static get VerifyInformationAsync() {
    return import(/* webpackChunkName: "VerifyInformation" */ './pages/onboarding/VerifyInformation');
  }

  static get WelcomeCompleteAsync() {
    return import(/* webpackChunkName: "WelcomeComplete" */ './pages/onboarding/WelcomeComplete');
  }

  static get VerifyEmailAsync() {
    return import(/* webpackChunkName: "VerifyEmail" */ './pages/onboarding/VerifyEmail');
  }

  static get InvestAmountAsync() {
    return import(/* webpackChunkName: "InvestAmount" */ './pages/invest/InvestAmount');
  }

  static get InvestDocumentsAsync() {
    return import(/* webpackChunkName: "InvestDocuments" */ './pages/invest/InvestDocuments');
  }

  static get InvestPaymentAsync() {
    return import(/* webpackChunkName: "InvestPayment" */ './pages/invest/InvestPayment');
  }

  static get InvestReviewAsync() {
    return import(/* webpackChunkName: "InvestReview" */ './pages/invest/InvestReview');
  }

  static get InvestConfirmationAsync() {
    return import(/* webpackChunkName: "InvestConfirmation" */ './pages/invest/InvestConfirmation');
  }

  static get InvestVerifyIdAsync() {
    return import(/* webpackChunkName: "InvestVerifyId" */ './pages/invest/InvestVerifyId');
  }
}
